import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  DriverFormValues,
  TransporterFormValues,
  VehicleFormValues,
} from "@/features/fleet/schema";
import type { Driver, Transporter, Vehicle } from "@/features/fleet/types";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

const text = (value: string | undefined) => value?.trim() || null;

function numOrNull(value: number | undefined) {
  if (value === undefined || value === null || Number.isNaN(value)) return null;
  return value;
}

export function useTransporters() {
  return useQuery({
    queryKey: ["transporters"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<Transporter[]> => {
      const { data, error } = await supabase
        .from("transporters")
        .select("*")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Transporter[];
    },
  });
}

export function useDrivers() {
  return useQuery({
    queryKey: ["drivers"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<Driver[]> => {
      const { data, error } = await supabase
        .from("drivers")
        .select("*, transporters(name)")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const t = raw.transporters as { name?: string } | null;
        const { transporters: _t, ...rest } = raw;
        return { ...(rest as Driver), transporter_name: t?.name ?? null };
      });
    },
  });
}

export function useVehicles() {
  return useQuery({
    queryKey: ["vehicles"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<Vehicle[]> => {
      const { data, error } = await supabase
        .from("vehicles")
        .select("*, transporters(name), drivers(name)")
        .order("vehicle_number", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const t = raw.transporters as { name?: string } | null;
        const d = raw.drivers as { name?: string } | null;
        const { transporters: _t, drivers: _d, ...rest } = raw;
        return {
          ...(rest as Vehicle),
          capacity_tons:
            (rest as Vehicle).capacity_tons == null
              ? null
              : Number((rest as Vehicle).capacity_tons),
          rate_per_km:
            (rest as Vehicle).rate_per_km == null
              ? null
              : Number((rest as Vehicle).rate_per_km),
          transporter_name: t?.name ?? null,
          driver_name: d?.name ?? null,
        };
      });
    },
  });
}

export function useUpsertTransporter() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: TransporterFormValues }) => {
      const payload = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        gstin: text(values.gstin),
        contact_person: text(values.contact_person),
        phone: text(values.phone),
        email: text(values.email),
        address: text(values.address),
        payment_terms: text(values.payment_terms),
        notes: text(values.notes),
        is_active: values.is_active,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (id) {
        const { data, error } = await supabase
          .from("transporters")
          .update(payload)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        return data as Transporter;
      }
      const { data, error } = await supabase
        .from("transporters")
        .insert({ ...payload, created_by: user?.id ?? null })
        .select("*")
        .single();
      if (error) throw error;
      return data as Transporter;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["transporters"] });
      toast.success("Transporter saved");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to save transporter"),
  });
}

export function useDeleteTransporter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("transporters").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["transporters"] });
      toast.success("Transporter deleted");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to delete"),
  });
}

export function useUpsertDriver() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: DriverFormValues }) => {
      const payload = {
        name: values.name.trim(),
        phone: text(values.phone),
        license_number: text(values.license_number),
        transporter_id: values.transporter_id?.trim() || null,
        status: values.status,
        notes: text(values.notes),
        is_active: values.is_active,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (id) {
        const { data, error } = await supabase
          .from("drivers")
          .update(payload)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        return data as Driver;
      }
      const { data, error } = await supabase
        .from("drivers")
        .insert({ ...payload, created_by: user?.id ?? null })
        .select("*")
        .single();
      if (error) throw error;
      return data as Driver;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Driver saved");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to save driver"),
  });
}

export function useDeleteDriver() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("drivers").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Driver deleted");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to delete"),
  });
}

export function useUpsertVehicle() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: VehicleFormValues }) => {
      const payload = {
        vehicle_number: values.vehicle_number.trim().toUpperCase(),
        vehicle_type: values.vehicle_type,
        capacity_tons: numOrNull(values.capacity_tons),
        transporter_id: values.transporter_id?.trim() || null,
        driver_id: values.driver_id?.trim() || null,
        status: values.status,
        rate_per_km: numOrNull(values.rate_per_km),
        notes: text(values.notes),
        is_active: values.is_active,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (id) {
        const { data, error } = await supabase
          .from("vehicles")
          .update(payload)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        return data as Vehicle;
      }
      const { data, error } = await supabase
        .from("vehicles")
        .insert({ ...payload, created_by: user?.id ?? null })
        .select("*")
        .single();
      if (error) throw error;
      return data as Vehicle;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Vehicle saved");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to save vehicle"),
  });
}

export function useDeleteVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("vehicles").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Vehicle deleted");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to delete"),
  });
}
